/**
 * Client support identification helpers (pure; no I/O).
 *
 * Two PARALLEL identification paths for support calls:
 *
 *  1. Brand + Store #  →  canonical Location  →  currentOperatorMasterAccountId
 *     Data path today: GET /sa/brands/{brandId}/locations (canonical Location
 *     rows carry storeNumber + current commercial operator). This module only
 *     matches within that response; it never invents relationships. The UI
 *     contract (Brand, Store #, LookupResult) is designed so the data source
 *     can later become a dedicated indexed SA lookup endpoint unchanged.
 *
 *  2. Find Client  →  free text across the Clients LIST PROJECTION only:
 *     canonical owner first/last name (kk-profile, projected by the list API
 *     as ownerFirstName/ownerLastName/ownerNameSource), owner email
 *     (active Owner membership), billing email (MasterAccount), company/
 *     business name, customer number. Pure function of the list rows — no
 *     per-Client HTTP reads.
 *
 * Location doctrine preserved: Brand owns Location; Master Account OPERATES it
 * (LocationAssignment). Resolving the current operator is identification only,
 * not authorization and not a new ownership model.
 */

import type { BrandLocationRow } from "@/features/brands/brandsApi";
import type { MasterAccountRow } from "@/features/accounts/accountsApi";

// ---------------------------------------------------------------------------
// Store # normalisation
// ---------------------------------------------------------------------------

/**
 * Normalise a store number for comparison.
 *
 * Tolerated presentation differences: surrounding whitespace, an optional
 * leading "#", and letter case (canonical storeNumber is an opaque string —
 * e.g. staging fixtures use "MAAL-QA-A").
 *
 * NOT collapsed: leading zeros. The canonical Location contract stores
 * storeNumber as an opaque string with no numeric normalisation anywhere in
 * the API (it is written and read verbatim), so "001321" and "1321" are
 * treated as different identifiers.
 */
export function normalizeStoreNumber(value: string | null | undefined): string {
  return String(value ?? "")
    .trim()
    .replace(/^#\s*/, "")
    .trim()
    .toUpperCase();
}

/**
 * What the operator typed for the Location path. Both are matched against
 * the SAME canonical Brand Location rows (they narrow/confirm one Location);
 * neither is ever applied to MasterAccount/company fields.
 */
export type LocationCriteria = {
  storeNumber: string;
  /** Location Address (physical store) — NOT MasterAccount company address. */
  address: string;
};

/** One candidate when several canonical Locations match the criteria. */
export type LocationCandidate = {
  location: BrandLocationRow;
  masterAccountId: string | null;
  businessName: string | null;
};

export type LocationLookupResult =
  | { kind: "idle" }
  | ({ kind: "needs-brand" } & LocationCriteria)
  | ({ kind: "loading" } & LocationCriteria)
  | ({ kind: "error"; message: string } & LocationCriteria)
  | ({ kind: "no-location" } & LocationCriteria)
  | ({ kind: "no-operator"; location: BrandLocationRow } & LocationCriteria)
  | ({
      kind: "resolved";
      location: BrandLocationRow;
      masterAccountId: string;
      businessName: string | null;
    } & LocationCriteria)
  /** >1 canonical Location matches — never silently pick one. */
  | ({ kind: "multiple"; candidates: LocationCandidate[] } & LocationCriteria);

/** @deprecated alias kept for the Store #-only call sites/tests. */
export type StoreLookupResult = LocationLookupResult;

/** Find the canonical Location(s) whose storeNumber matches (post-normalisation). */
export function matchLocationsByStoreNumber(
  locations: BrandLocationRow[] | null | undefined,
  storeNumber: string,
): BrandLocationRow[] {
  const wanted = normalizeStoreNumber(storeNumber);
  if (!wanted) return [];
  return (locations || []).filter(
    (loc) => normalizeStoreNumber(loc.storeNumber) === wanted,
  );
}

// ---------------------------------------------------------------------------
// Location Address matching (canonical Location address_* fields ONLY)
// ---------------------------------------------------------------------------

/**
 * Tokenise support-call address input: split on whitespace/commas, strip
 * leading/trailing punctuation ("St." → "st", "#12" → "12"), lower-case.
 * No abbreviation/synonym rules (St ≠ Street is NOT asserted either way —
 * "st" simply matches inside "street" by substring), so distinct Locations
 * are never merged by an invented normalisation.
 */
export function addressTokens(query: string | null | undefined): string[] {
  return String(query ?? "")
    .toLowerCase()
    .split(/[\s,]+/)
    .map((t) => t.replace(/^[^a-z0-9]+|[^a-z0-9]+$/g, ""))
    .filter(Boolean);
}

/** Canonical Location address haystack: street, city, state, ZIP, country. */
export function locationAddressHaystack(loc: BrandLocationRow): string {
  return [
    loc.address_street,
    loc.address_city,
    loc.address_state,
    loc.address_zipCode,
    loc.address_country,
    // Formatted fallback for rows that only carry the composed string.
    loc.address,
  ]
    .map((v) => String(v ?? "").toLowerCase())
    .filter(Boolean)
    .join(" ")
    .replace(/\s+/g, " ");
}

/**
 * Locations whose canonical address contains EVERY typed token (AND). Partial
 * and case-insensitive: "123 Main", "123 Main St", "Peachtree", "Atlanta",
 * "30523" all work. Multiple rows can legitimately match — the caller must
 * surface that honestly (see resolveLocationLookup → "multiple").
 */
export function matchLocationsByAddress(
  locations: BrandLocationRow[] | null | undefined,
  query: string,
): BrandLocationRow[] {
  const tokens = addressTokens(query);
  if (!tokens.length) return [];
  return (locations || []).filter((loc) => {
    const hay = locationAddressHaystack(loc);
    return hay.length > 0 && tokens.every((t) => hay.includes(t));
  });
}

function toCandidate(location: BrandLocationRow): LocationCandidate {
  const maId = String(location.currentOperatorMasterAccountId || "").trim();
  return {
    location,
    masterAccountId: maId || null,
    businessName: location.currentOperatorBusinessName ?? null,
  };
}

/**
 * Resolve Brand + (Store # and/or Location Address) against that Brand's
 * canonical Location rows. Both criteria narrow the SAME Location set.
 *
 * - 0 matches   → no-location
 * - >1 matches  → multiple (never silently pick one) — except the legacy
 *                 Store#-only case where duplicate storeNumbers are a data
 *                 anomaly (unique per Brand by index): keep preferring the
 *                 row with a current operator.
 * - 1 match     → resolved, or no-operator (never fabricates a Client)
 */
export function resolveLocationLookup(
  locations: BrandLocationRow[] | null | undefined,
  criteria: Partial<LocationCriteria>,
): LocationLookupResult {
  const storeNumber = String(criteria.storeNumber ?? "").trim();
  const address = String(criteria.address ?? "").trim();
  if (!storeNumber && !address) return { kind: "idle" };
  const c: LocationCriteria = { storeNumber, address };

  let matches: BrandLocationRow[] = (locations || []).slice();
  if (storeNumber) matches = matchLocationsByStoreNumber(matches, storeNumber);
  if (address) matches = matchLocationsByAddress(matches, address);

  if (matches.length === 0) return { kind: "no-location", ...c };
  if (matches.length > 1 && address) {
    return { kind: "multiple", candidates: matches.map(toCandidate), ...c };
  }
  const location =
    matches.find((m) => m.currentOperatorMasterAccountId) || matches[0];
  const maId = String(location.currentOperatorMasterAccountId || "").trim();
  if (!maId) return { kind: "no-operator", location, ...c };
  return {
    kind: "resolved",
    location,
    masterAccountId: maId,
    businessName: location.currentOperatorBusinessName ?? null,
    ...c,
  };
}

/**
 * Resolve Brand + Store # against that Brand's canonical Location rows.
 * (Store #-only convenience over resolveLocationLookup.)
 */
export function resolveStoreLookup(
  locations: BrandLocationRow[] | null | undefined,
  storeNumber: string,
): StoreLookupResult {
  return resolveLocationLookup(locations, { storeNumber, address: "" });
}

/** Distinct current-operator ids across candidate Locations (multiple-match scope). */
export function candidateMasterAccountIds(candidates: LocationCandidate[]): string[] {
  const ids = new Set<string>();
  for (const c of candidates) if (c.masterAccountId) ids.add(c.masterAccountId);
  return Array.from(ids);
}

// ---------------------------------------------------------------------------
// Brand alone = Client Brand PORTFOLIO (canonical BrandMembership), NOT
// Location current-operator and NOT MAAL. Answers "which Clients have this
// Brand in their portfolio?" from the already-loaded Clients list rows
// (row.brandIds). A Client with the Brand in its portfolio but zero Locations
// MUST match here (Owner QA: Chateau Villa / McDonald's).
// ---------------------------------------------------------------------------

/** Master Account ids whose canonical Brand portfolio contains brandId. */
export function portfolioMasterAccountIds(
  rows: Pick<MasterAccountRow, "masterAccountId" | "brandIds">[] | null | undefined,
  brandId: string,
): string[] {
  const id = String(brandId || "").trim();
  if (!id) return [];
  const out: string[] = [];
  for (const row of rows || []) {
    if (Array.isArray(row.brandIds) && row.brandIds.includes(id)) {
      out.push(row.masterAccountId);
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Find Client (free text)
// ---------------------------------------------------------------------------

export type FindClientMatchField =
  | "ownerEmail"
  | "billingEmail"
  | "ownerName"
  | "businessName"
  | "customerNumber";

export type FindClientMatch = {
  field: FindClientMatchField;
  /** Human label, e.g. "Owner Email". */
  label: string;
  /** The matched value as displayed (never anything beyond the field itself). */
  value: string;
  exact: boolean;
  /** For ownerName: whether the canonical kk-profile name or the membership snapshot matched. */
  nameSource?: "kk-profile" | "membership-snapshot";
};

export type FindClientHit = {
  account: MasterAccountRow;
  matches: FindClientMatch[];
  /** Lower = stronger. */
  rank: number;
};

const FIELD_LABELS: Record<FindClientMatchField, string> = {
  ownerEmail: "Owner Email",
  billingEmail: "Billing Email",
  ownerName: "Owner Name",
  businessName: "Company",
  customerNumber: "Customer #",
};

function norm(v: string | null | undefined): string {
  return String(v ?? "")
    .trim()
    .toLowerCase();
}

function squash(v: string | null | undefined): string {
  return norm(v).replace(/\s+/g, " ");
}

export function looksLikeEmail(query: string): boolean {
  return /\S+@\S+/.test(String(query || "").trim());
}

/**
 * Name match: every whitespace-separated query token must appear in the
 * person's full name ("first last"). Supports first only, last only, or
 * first + last in either order.
 */
export function nameMatches(
  fullName: string | null | undefined,
  query: string,
): { matched: boolean; exact: boolean } {
  const name = squash(fullName);
  const q = squash(query);
  if (!name || !q) return { matched: false, exact: false };
  const tokens = q.split(" ").filter(Boolean);
  const matched = tokens.every((t) => name.includes(t));
  return { matched, exact: matched && name === q };
}

/**
 * Searchable owner name for a list row, from the list projection only.
 *
 *  - ownerNameSource "kk-profile"  → canonical first/last; the stale membership
 *    snapshot is not present in the row and cannot produce a hit.
 *  - ownerNameSource "membership"  → provisioning snapshot is searchable ONLY
 *    because no canonical identity exists yet; labelled as such.
 *  - ownerNameSource "none" / absent name → not name-searchable (the email
 *    fallback in ownerDisplayName is matched via the email fields instead).
 *  - Legacy rows without ownerNameSource (older API) → ownerDisplayName is
 *    treated as a membership snapshot.
 */
export function composeOwnerName(
  account: Pick<
    MasterAccountRow,
    "ownerFirstName" | "ownerLastName" | "ownerNameSource" | "ownerDisplayName" | "ownerEmail"
  >,
): { name: string; source: "kk-profile" | "membership-snapshot" } | null {
  const source = account.ownerNameSource ?? null;
  const fromParts = `${account.ownerFirstName || ""} ${account.ownerLastName || ""}`.trim();
  if (source === "kk-profile") {
    return fromParts ? { name: fromParts, source: "kk-profile" } : null;
  }
  if (source === "membership") {
    return fromParts ? { name: fromParts, source: "membership-snapshot" } : null;
  }
  if (source === "none") return null;
  // Legacy projection (no ownerNameSource): ownerDisplayName is the membership
  // snapshot or an email fallback; never search the email here.
  const legacy = String(account.ownerDisplayName || "").trim();
  if (!legacy || legacy === String(account.ownerEmail || "").trim()) return null;
  return { name: legacy, source: "membership-snapshot" };
}

/**
 * Evaluate ONE account against a Find Client query.
 * Exact email input restricts to exact email matches (narrowest result).
 */
export function evaluateFindClient(
  account: MasterAccountRow,
  query: string,
): FindClientMatch[] {
  const q = norm(query);
  if (!q) return [];
  const matches: FindClientMatch[] = [];

  const emailQuery = looksLikeEmail(q);
  const pushEmail = (field: "ownerEmail" | "billingEmail", value: string | null) => {
    const v = norm(value);
    if (!v) return;
    if (v === q) {
      matches.push({ field, label: FIELD_LABELS[field], value: String(value), exact: true });
    } else if (!emailQuery && v.includes(q)) {
      matches.push({ field, label: FIELD_LABELS[field], value: String(value), exact: false });
    } else if (emailQuery && v.includes(q)) {
      // Partial email typed (e.g. "jane@comp") — allow contains until it is exact.
      matches.push({ field, label: FIELD_LABELS[field], value: String(value), exact: false });
    }
  };
  pushEmail("ownerEmail", account.ownerEmail);
  pushEmail("billingEmail", account.billingEmail);

  // Exact email typed and matched → that is the answer; skip weaker fields.
  if (emailQuery && matches.some((m) => m.exact)) {
    return matches.filter((m) => m.exact);
  }

  const cn = norm(account.customerNumber);
  if (cn && cn.includes(q)) {
    matches.push({
      field: "customerNumber",
      label: FIELD_LABELS.customerNumber,
      value: String(account.customerNumber),
      exact: cn === q,
    });
  }

  const owner = composeOwnerName(account);
  if (owner) {
    const nm = nameMatches(owner.name, q);
    if (nm.matched) {
      matches.push({
        field: "ownerName",
        label: FIELD_LABELS.ownerName,
        value: owner.name,
        exact: nm.exact,
        nameSource: owner.source,
      });
    }
  }

  const biz = squash(account.businessName);
  if (biz && biz.includes(squash(q))) {
    matches.push({
      field: "businessName",
      label: FIELD_LABELS.businessName,
      value: account.businessName,
      exact: biz === squash(q),
    });
  }

  return matches;
}

const FIELD_RANK: Record<FindClientMatchField, number> = {
  ownerEmail: 0,
  billingEmail: 1,
  customerNumber: 2,
  ownerName: 3,
  businessName: 4,
};

/** Filter + rank accounts for a Find Client query. Empty query → all rows, no matches. */
export function findClientHits(
  rows: MasterAccountRow[],
  query: string,
): FindClientHit[] {
  const q = String(query || "").trim();
  if (!q) return rows.map((account) => ({ account, matches: [], rank: 99 }));
  const hits: FindClientHit[] = [];
  for (const account of rows) {
    const matches = evaluateFindClient(account, q);
    if (!matches.length) continue;
    const rank = Math.min(
      ...matches.map((m) => FIELD_RANK[m.field] - (m.exact ? 10 : 0)),
    );
    hits.push({ account, matches, rank });
  }
  hits.sort((a, b) => a.rank - b.rank);
  return hits;
}
